import { SpaceBetween } from "@cloudscape-design/components-themed";
import { useEffect, useMemo, useState } from "react";
import ExaminationComment from "./ExaminationComment";
import ExaminationResultApproval from "./ExaminationResultApproval";
import Footer from "./Footer";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../app/hook";
import { showModal } from "../UI/modal/modal-slice";
import {
  ActionPattern,
  COMMON_LABELS,
  ApprovalStatusPattern,
} from "../default-constants";
import { COMMENT_MAX_LENGTH, EXAMINATION_RESULT } from "./constants";
import { ExaminationResultHandlingRegistrationsPutRequestItem } from "../../features/api-interfaces";
import { getCurrentStage } from "./util";
import {
  useGetAuthQuery,
  useGetExaminationResultHandlingRegistrationsQuery,
  usePutExaminationResultHandlingRegistrationsMutation,
} from "../../features/api";
import { isLengthValid } from "../../utils/inputValidation";
import { addNotification } from "../../features/error/notifications-slice";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import { PERMISSIONS } from "../../configs/constants";

/**
 * 審査結果取扱登録
 */
const Main = () => {
  const dispatch = useAppDispatch();
  const navigator = useNavigate();
  const { examinationApplicationNo } = useParams();
  const isExamine = useHasAuthorityHook(PERMISSIONS.EXAMINE);
  const isMasterApproval = useHasAuthorityHook(PERMISSIONS.MASTER_APPROVAL);
  const isDocomoAdminUser = !!isExamine && !!isMasterApproval;
  const { loginUser } = useGetAuthQuery(undefined, {
    selectFromResult: ({ data }: any) => ({
      loginUser: `${data?.familyName}${data?.givenName}`,
    }),
  });
  const {
    data: apiResponse,
    isError,
    error,
    refetch,
  } = useGetExaminationResultHandlingRegistrationsQuery(
    examinationApplicationNo!
  );
  const err: any = error;
  const [putExaminationResultHandlingRegistrations] =
    usePutExaminationResultHandlingRegistrationsMutation();
  const currentStage = useMemo(
    () =>
      getCurrentStage(
        apiResponse?.approvalAction,
        apiResponse?.actionBy,
        loginUser,
        isDocomoAdminUser
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loginUser, apiResponse]
  );
  const [inputExaminationComment, setInputExaminationComment] = useState("");
  const [selectedExaminationResult, setSelectedExaminationResult] = useState({
    value: EXAMINATION_RESULT.ok.value,
    label: EXAMINATION_RESULT.ok.label,
  });
  const [inputActionComment, setInputActionComment] = useState("");

  useEffect(() => {
    if (isError && "data" in err) {
      const { messageId, errorDetail } = err.data;
      if (messageId) {
        dispatch(
          addNotification({
            msgId: messageId,
            msgArgs: errorDetail,
          })
        );
      }
      backOnClick();
    }
    setInputExaminationComment(apiResponse?.examinationComment ?? "");
    setInputActionComment(apiResponse?.actionComment ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /**
   * 戻るボタンを押したときの処理.
   */
  const backOnClick = () => {
    navigator(`/examination-inquiry/${examinationApplicationNo}`);
  };

  /**
   * アクション実行メソッド.
   * @param {string} content
   * @param {{ approvalAction: number; examinationComment?: string; examinationResult?: number; actionComment?: string; }} requestData
   */
  const execute = (
    content: string,
    requestData: {
      approvalAction: number;
      examinationComment?: string;
      examinationResult?: number;
      actionComment?: string;
    }
  ) => {
    // 不正入力の場合はアクションを起こさない
    if (
      (requestData.examinationComment !== undefined &&
        !isLengthValid(requestData.examinationComment, COMMENT_MAX_LENGTH)) ||
      (requestData.actionComment !== undefined &&
        !isLengthValid(requestData.actionComment, COMMENT_MAX_LENGTH))
    ) {
      return;
    }
    putAction(content, requestData);
  };

  /**
   * 審査結果取扱情報更新APIを実行する.
   * @param {string} content
   * @param {{ approvalAction: number; examinationComment?: string; examinationResult?: number; actionComment?: string; }} requestData
   */
  const putAction = (
    content: string,
    requestData: {
      approvalAction: number;
      examinationComment?: string;
      examinationResult?: number;
      actionComment?: string;
    }
  ) => {
    dispatch(
      showModal({
        content,
        positiveFn: () => {
          const request: ExaminationResultHandlingRegistrationsPutRequestItem =
            {
              examinationApplicationNo: examinationApplicationNo!,
              data: {
                approvalFlowNo: apiResponse!.approvalFlowNo,
                approvalStatus: apiResponse!.approvalStatus
                  ? apiResponse!.approvalStatus
                  : ApprovalStatusPattern.beforeApproval.statusCode,
                ...requestData,
              },
            };
          putExaminationResultHandlingRegistrations(request).then(
            (result: any) => {
              if ("error" in result && "data" in result.error) {
                const { messageId, errorDetail } = result.error.data;
                if (messageId) {
                  dispatch(
                    addNotification({
                      msgId: messageId,
                      msgArgs: errorDetail ?? [],
                    })
                  );
                }
              } else {
                navigator("/examination-status?isBackFromDetail=true");
              }
            }
          );
        },
      })
    );
  };

  /**
   * 承認依頼ボタンを押したときの処理.
   */
  const approvalRequestOnClick = () => {
    const requestData = {
      approvalAction: ActionPattern.approvalRequest.statusCode,
      examinationComment: inputExaminationComment,
    };
    execute(COMMON_LABELS.CONTENTS.APPROVAL_REQUEST, requestData);
  };

  /**
   * 引戻ボタンを押したときの処理.
   */
  const pullbackOnClick = () => {
    const requestData = {
      approvalAction: ActionPattern.pullBack.statusCode,
      examinationComment: "",
    };
    execute(COMMON_LABELS.CONTENTS.PULL_BACK, requestData);
  };

  /**
   * 差戻ボタンを押したときの処理.
   */
  const sendBackOnClick = () => {
    const requestData = {
      approvalAction: ActionPattern.sendBack.statusCode,
      examinationComment: "",
      actionComment: inputActionComment,
    };
    execute(COMMON_LABELS.CONTENTS.SEND_BACK, requestData);
  };

  /**
   * 承認ボタンを押したときの処理.
   */
  const approvalOnClick = () => {
    const requestData = {
      approvalAction: ActionPattern.approval.statusCode,
      examinationComment: inputExaminationComment,
      examinationResult: Number(selectedExaminationResult.value),
      actionComment: inputActionComment,
    };
    execute(COMMON_LABELS.CONTENTS.APPROVAL, requestData);
  };

  return (
    <SpaceBetween direction={"vertical"} size={"l"}>
      <ExaminationComment
        currentStage={currentStage}
        examinationCommentDocomo={apiResponse?.examinationCommentDocomo ?? ""}
        examinationDecisionCode={apiResponse?.examinationDecisionCode ?? ""}
        examinationCommentVmtf={apiResponse?.examinationCommentVmTf ?? ""}
        examinationCommentVmsmcc={apiResponse?.examinationCommentVmSmcc ?? ""}
        examinationCommentVmsaison={
          apiResponse?.examinationCommentVmSaison ?? ""
        }
        examinationCommentJcb={apiResponse?.examinationCommentJcb ?? ""}
        examinationCommentAmex={apiResponse?.examinationCommentAmex ?? ""}
        examinationComment={inputExaminationComment}
        setExaminationComment={setInputExaminationComment}
        actionComment={apiResponse?.actionComment ?? ""}
      />
      <ExaminationResultApproval
        currentStage={currentStage}
        selectedExaminationResult={selectedExaminationResult}
        setSelectedExaminationResult={setSelectedExaminationResult}
        examinationResult={apiResponse?.examinationResult?.toString() ?? ""}
        actionAt={apiResponse?.actionAt ?? ""}
        actionBy={apiResponse?.actionBy ?? ""}
        loginUser={loginUser}
        approvalComment={inputActionComment}
        setApprovalComment={setInputActionComment}
      />
      <Footer
        currentStage={currentStage}
        examinationStatus={apiResponse?.examinationStatus ?? -1}
        backOnClick={backOnClick}
        approvalRequestOnClick={approvalRequestOnClick}
        pullbackOnClick={pullbackOnClick}
        sendBackOnClick={sendBackOnClick}
        approvalOnClick={approvalOnClick}
        examinationPossibleFlag={apiResponse?.examinationPossibleFlag ?? false}
      />
    </SpaceBetween>
  );
};

export default Main;
